import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Link as MuiLink,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import PageLayout from '../../components/layout/PageLayout';
import PageHeader from '../../components/layout/PageHeader';
import { useAuth } from '../../context/AuthContext';
import { apiCall } from '../../utils/fetchAPI';
import { translateApiMessage, translateViolations } from '../../utils/apiMessages';

const EMPTY = { name: '', email: '', subject: '', body: '' };

/** The fields this form shows, and so the only ones an error can sit under. */
const FIELDS = ['name', 'email', 'subject', 'body'];

/**
 * Writing to the people who run the site.
 *
 * Who reads this is worth being exact about: an administrator, who looks after
 * Vie de Quartier — accounts, listings, what gets published — and who runs nothing
 * in the neighbourhood itself. The copy says so, because somebody writing here
 * about a leaking pipe or a lost cat is writing to the wrong place, and the
 * feed is where their neighbours are.
 *
 * Deliberately not a `mailto:` link. An email is a message with no state:
 * nobody can tell whether it was answered, and the second moderator to open
 * the inbox has no idea the first already replied. This goes into the back
 * office, where it can be marked as dealt with.
 *
 * Open to visitors: somebody who cannot sign in — a forgotten password, a
 * suspended account, a professional whose listing was refused — is exactly the
 * person most likely to need it. Signed in, the name and address are filled in
 * for them, and they stay theirs to change: the reply goes where they say.
 */
function Contact() {
  const { user } = useAuth();

  const [form, setForm] = useState(EMPTY);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!user) {
      return;
    }

    setForm((previous) => ({
      ...previous,
      name: previous.name || user.username || '',
      email: previous.email || user.email || '',
    }));
  }, [user]);

  const update = (e) => {
    const { name, value } = e.target;

    setForm((previous) => ({ ...previous, [name]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});

    const missing = {};

    if (!form.name.trim()) missing.name = 'Dites-nous qui vous êtes';
    if (!form.email.trim()) missing.email = 'Laissez une adresse email pour la réponse';
    if (!form.subject.trim()) missing.subject = 'Résumez votre message en quelques mots';
    if (!form.body.trim()) missing.body = 'Écrivez votre message';

    if (Object.keys(missing).length > 0) {
      setFieldErrors(missing);
      setError('Corrigez les champs signalés ci-dessous');
      return;
    }

    setSending(true);

    try {
      const response = await apiCall('/contact', { method: 'POST', body: JSON.stringify(form) });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const violations = translateViolations(data.violations);
        setFieldErrors(violations);

        if (0 === Object.keys(violations).filter((field) => FIELDS.includes(field)).length) {
          setError(translateApiMessage(data.error) || 'Votre message n’a pas pu être envoyé.');
        }

        return;
      }

      setSent(true);
      setForm(EMPTY);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <PageLayout centered={false}>
      <Box sx={{ width: '100%', maxWidth: 680 }}>
        <PageHeader
          title="Nous écrire"
          lede="Une question sur le site, un souci avec votre compte ou votre fiche, un contenu à nous signaler : l’équipe qui gère Vie de Quartier vous répondra."
        />

        <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3.5 } }} component="form" onSubmit={submit} noValidate>
          {sent && (
            <Alert severity="success" sx={{ mb: 2.5 }}>
              Votre message est parti. Nous vous répondrons à l’adresse que vous avez indiquée.
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2.5 }}>
              {error}
            </Alert>
          )}

          <Stack spacing={2.5}>
            <TextField
              label="Votre nom"
              name="name"
              value={form.name}
              onChange={update}
              error={Boolean(fieldErrors.name)}
              helperText={fieldErrors.name}
              disabled={sending}
              required
              fullWidth
            />

            <TextField
              label="Votre adresse email"
              name="email"
              type="email"
              value={form.email}
              onChange={update}
              error={Boolean(fieldErrors.email)}
              helperText={fieldErrors.email || 'C’est là que la réponse arrivera'}
              disabled={sending}
              required
              fullWidth
            />

            <TextField
              label="Sujet"
              name="subject"
              value={form.subject}
              onChange={update}
              error={Boolean(fieldErrors.subject)}
              helperText={fieldErrors.subject || 'Par exemple : « Ma fiche est en attente depuis deux semaines »'}
              disabled={sending}
              required
              fullWidth
              inputProps={{ maxLength: 180 }}
            />

            <TextField
              label="Votre message"
              name="body"
              value={form.body}
              onChange={update}
              error={Boolean(fieldErrors.body)}
              helperText={fieldErrors.body || 'Donnez-nous de quoi vous aider : une adresse de page, un nom de fiche, ce que vous avez essayé.'}
              disabled={sending}
              required
              fullWidth
              multiline
              minRows={6}
              inputProps={{ maxLength: 5000 }}
            />
          </Stack>

          <Button
            type="submit"
            variant="contained"
            size="large"
            startIcon={<SendIcon />}
            disabled={sending}
            sx={{ mt: 3 }}
          >
            {sending ? 'Envoi…' : 'Envoyer'}
          </Button>

          <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'text.secondary' }}>
            Votre message est lu par l’équipe qui gère le site, et n’est jamais publié. Elle ne
            s’occupe pas de la vie du quartier : pour vous adresser à vos voisins, écrivez dans{' '}
            <MuiLink component={RouterLink} to="/quartier">
              le fil du quartier
            </MuiLink>
            .
          </Typography>
        </Paper>
      </Box>
    </PageLayout>
  );
}

export default Contact;
